import { createContext, useContext, useEffect, useMemo } from 'react';
import type { ReactNode } from 'react';

import { useAtom } from 'jotai';

import { useMyTenants } from '@/api/tenant';
import { currentTenantAtom } from '@/constants/currrentTenant';
import {
  findCurrentTenant,
  findSingleTenant,
} from '@/features/tenants/utils/find-tenant-with-parent';
import { useFlag, useFlagPayload } from '@/hooks/use-flag';
import { useTenantTheme } from '@/shared/hooks/useTenantTheme';

type TenantBrandingContextValue = {
  logoUrl: string | null;
};

type TenantBrandingFlagPayload = {
  enabledTenantIds?: string[];
};

const TenantBrandingContext = createContext<TenantBrandingContextValue>({
  logoUrl: null,
});

export const TenantBrandingProvider = ({ children }: { children: ReactNode }) => {
  const { data: tenants } = useMyTenants();
  const [currentTenant, setCurrentTenant] = useAtom(currentTenantAtom);
  const isTenantBrandingFlagEnabled = useFlag('tenant-branding');
  const flagPayload = useFlagPayload<TenantBrandingFlagPayload>('tenant-branding');

  const selectedTenant = useMemo(
    () => findCurrentTenant(tenants, currentTenant?.id),
    [tenants, currentTenant?.id]
  );
  const fallbackBrandingTenant = useMemo(
    () => (currentTenant?.id ? undefined : findSingleTenant(tenants)),
    [tenants, currentTenant?.id]
  );
  const brandingTenant = selectedTenant ?? fallbackBrandingTenant;
  const brandingTenantId = brandingTenant?.id;
  const enabledTenantIds = flagPayload?.enabledTenantIds;
  const isTenantBrandingEnabled =
    isTenantBrandingFlagEnabled &&
    (brandingTenantId
      ? enabledTenantIds
        ? enabledTenantIds.includes(brandingTenantId)
        : true
      : false);

  useEffect(() => {
    if (!tenants.length) {
      setCurrentTenant(undefined);
      return;
    }

    if (selectedTenant) {
      if (currentTenant?.id !== selectedTenant.id || currentTenant?.name !== selectedTenant.name) {
        setCurrentTenant({
          id: selectedTenant.id,
          name: selectedTenant.name,
        });
      }
      return;
    }

    if (currentTenant?.id) {
      setCurrentTenant(undefined);
    }
  }, [tenants, selectedTenant, currentTenant?.id, currentTenant?.name, setCurrentTenant]);

  const { themeVars, logoUrl } = useTenantTheme(
    isTenantBrandingEnabled && brandingTenant
      ? {
          ...brandingTenant.colors,
          logoId: brandingTenant.logoId,
          name: brandingTenant.name,
        }
      : undefined
  );

  useEffect(() => {
    const entries = Object.entries(themeVars);

    for (const [key, value] of entries) {
      document.body.style.setProperty(key, String(value));
    }

    return () => {
      for (const [key] of entries) {
        document.body.style.removeProperty(key);
      }
    };
  }, [themeVars]);

  return (
    <TenantBrandingContext.Provider value={{ logoUrl }}>
      <div style={themeVars}>{children}</div>
    </TenantBrandingContext.Provider>
  );
};

export const useTenantBranding = () => useContext(TenantBrandingContext);
